import {
  Box,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  InputGroup,
  InputRightElement,
  VStack,
} from "@chakra-ui/react"
import FormInput from "@components/Forms/FormInput"
import H1 from "@components/Headings/H1"
import Layout from "@components/Layouts"
import BrandButton from "@components/Links/BrandButton"
import TextLink from "@components/Links/TextLink"
import { useStackContext } from "@context/StackContext"
import { authOptions } from "@pages/api/auth/[...nextauth]"
import type { NextPage } from "next"
import { GetServerSideProps } from "next"
import { useRouter } from "next/router"
import { getServerSession } from "next-auth"
import { signIn } from "next-auth/react"
import { NextSeo } from "next-seo"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FaEye, FaEyeSlash } from "react-icons/fa"

const Signin: NextPage = () => {
  const { stackData } = useStackContext()
  const [formError, setFormError] = useState(false)
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const handleShowPass = () => setShowPass(!showPass)
  const router = useRouter()

  // React Hook Form
  type FormData = {
    email: string
    password: string
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  async function onSubmit(values: FormData) {
    try {
      setFormError(false)
      setFormSubmitting(true)
      await signIn("credentials", {
        redirect: false,
        email: values.email,
        password: values.password,
      }).then((res) => {
        if (res?.error) {
          setFormSubmitting(false)
          setFormError(true)
        } else {
          if (stackData.berth) {
            window.location.href =
              (router.query.callbackUrl as string) || `/applications/new`
          } else {
            window.location.href = (router.query.callbackUrl as string) || `/`
          }
        }
      })
    } catch (error) {
      setFormSubmitting(false)
      console.error(error)
      setFormError(true)
    }
  }

  return (
    <Layout>
      <NextSeo title={"Sign In"} />
      <Container maxW={"container.sm"}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={8}>
            <H1>{"Sign in to your account"}</H1>

            <HStack
              justifyItems={"start"}
              w={"full"}
            >
              <Box>{"Don't have an account?"}</Box>
              <TextLink href={"/signup"}>{"Sign up"}</TextLink>
            </HStack>

            <FormControl isInvalid={!!errors.email}>
              <FormInput
                id={"email"}
                placeholder={"Email"}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value:
                      // eslint-disable-next-line no-control-regex
                      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                    message: "Email is not valid",
                  },
                })}
                type={"email"}
                autoComplete={"email"}
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <InputGroup>
                <FormInput
                  id={"password"}
                  placeholder={"Password"}
                  {...register("password", {
                    required: "Password is required",
                  })}
                  type={showPass ? "text" : "password"}
                  isInvalid={!!errors.password}
                />
                <InputRightElement
                  as={Flex}
                  justifyContent={"center"}
                  alignItems={"center"}
                  h={"full"}
                >
                  <IconButton
                    aria-label={"Show password"}
                    variant={"ghost"}
                    icon={showPass ? <FaEye /> : <FaEyeSlash />}
                    onClick={handleShowPass}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>
                {errors.password && errors.password.message}
              </FormErrorMessage>
            </FormControl>

            <Box w={"full"}>
              <TextLink href={"/forgot-password"}>
                {"Forgot password?"}
              </TextLink>
            </Box>

            <BrandButton
              type={"submit"}
              isLoading={formSubmitting || isSubmitting}
            >
              {"Sign In"}
            </BrandButton>
            {formError && (
              <Box
                py={6}
                px={2}
                mb={8}
                color={"red.500"}
                borderColor={"red.500"}
                borderWidth={1}
                textAlign={"center"}
                w={"full"}
              >
                {"Incorrect email or password"}
              </Box>
            )}
          </VStack>
        </form>
      </Container>
    </Layout>
  )
}

export default Signin

export const getServerSideProps: GetServerSideProps = async ({ req, res }) => {
  const session = await getServerSession(req, res, authOptions)

  if (session && session?.error !== "RefreshAccessTokenError")
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    }

  return {
    props: { session },
  }
}
